import { CSSProperties, useEffect } from 'react';
import PresaveMiniature from '../PresaveMiniature';
import { PresaveWebsiteStyle } from 'modules/Types';
import { MiniatureContainer } from './styles';
import Logger from 'Logger';
import useScreen from 'Hooks/useScreen';
import { track } from 'analytics';
import { SymphonyTooltip } from "modules/Sty";
import { CampaignStatus, SYMPHONY_WEBSITE_URL } from 'modules/Const';
import ButtonSymphony from 'elements/ButtonSymphony';
import CardSymphony from 'elements/CardSymphony';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import { clsx, showToast } from 'modules/Utils';

interface PresaveLinkProps {
    className?: string;
    presaveImage: string;
    style: PresaveWebsiteStyle;
    status: string;
    contentSlug: string;
    songName: string;
    brandName: string;
    campaignId: number;
    brandId: number;
    brandSlug: string;
    daysLeft: number;
    canEdit?: boolean;
    openPresaveModal: () => void;
    closePresaveModal: () => void;
    onOpenEditLinkModal?: () => void;
}

const PresaveLink = ({
    className,
    presaveImage,
    style,
    contentSlug,
    status,
    songName,
    brandName,
    campaignId,
    brandId,
    brandSlug,
    daysLeft,
    canEdit = true,
    openPresaveModal,
    closePresaveModal,
    onOpenEditLinkModal
}: PresaveLinkProps) => {
    const { mobileView, mStyle } = useScreen();
    const showShareButton = Boolean(navigator.share) && canEdit;

    const isFinished = status === CampaignStatus.FINISHED;
    const constructLink = `${SYMPHONY_WEBSITE_URL}/${brandSlug}/${contentSlug}`

    useEffect(() => {
        if (!mobileView) {
            closePresaveModal();
        }
    }, [mobileView]);

    const onClickCopy = (source: string) => {
        track("Copied Pre-save URL", {
            campaign_id: campaignId,
            campaign_type: 'pre_save',
            brand_id: brandId,
            brand_slug: brandSlug,
            days_left: daysLeft,
            source,
        })
        navigator.clipboard.writeText(constructLink)
        showToast({ 
            message: `Your ${isFinished ? 'website' : 'pre-save'} URL was copied to your clipboard.`, 
            mobile: mobileView 
        })
    }

    const onClickShare = () => {
        track("Shared Pre-save URL", {
            campaign_id: campaignId,
            campaign_type: 'pre_save',
            brand_id: brandId,
            brand_slug: brandSlug,
            days_left: daysLeft,
        })
        try {
            navigator.share({
                title: `${isFinished ? "Listen to" : "Presave"} ${songName} by ${brandName}`,
                text: `${isFinished ? "Listen to" : "Presave"} ${songName} by ${brandName}`,
                url: constructLink,
            })
        } catch (e) {
            Logger.error('This action is only suported on mobiles')
        }
    }

    const renderMiniature = () => {
        return (
            <MiniatureContainer className="miniatureContainer">
                <PresaveMiniature image={presaveImage} style={style} />
            </MiniatureContainer>
        )
    }

    return (
        <CardSymphony
            className={clsx('preSaveLinkComponent', className)}
            title={isFinished ? 'Website Link' : 'Pre-save Link'}
            size="small"
        >
            <div
                className="preSaveLinkInnerContent"
                style={mStyle(sty, 'preSaveLinkInnerContent')}
            >
                {mobileView && renderMiniature()}
                <div className="preSaveLinkContainer" style={{ width: '100%' }}>
                    <div 
                        className="presaveLinkClickableContainer"
                        style={mStyle(sty, 'presaveLinkClickableContainer')}
                        onClick={mobileView ? () => onClickCopy('input') : undefined}
                    >
                        <div className="presaveLinkText" style={sty.presaveLinkText}>{constructLink}</div>
                        {!mobileView &&
                            <div className="buttonsContainerPreSave" style={sty.buttonsContainerPreSave}>
                                {showShareButton && (
                                    <ButtonSymphony
                                        className="presaveLinkShareButton"
                                        variant="text"
                                        onClick={onClickShare}
                                    >
                                        Share
                                    </ButtonSymphony>
                                )}
                                <ButtonSymphony
                                    className="presaveLinkCopyButton"
                                    variant="text"
                                    onClick={() => onClickCopy('button')}
                                >
                                    Copy
                                </ButtonSymphony>
                                {canEdit &&
                                    <SymphonyTooltip title="Edit URL Slug">
                                        <Icon className="editIconLink" icon="edit" onClick={onOpenEditLinkModal} />
                                    </SymphonyTooltip>
                                }
                            </div>
                        }
                    </div>
                    {mobileView &&
                        <div className="mobileButtonsContainer" style={sty.mobileButtonsContainer}>
                            <ButtonSymphony
                                className="viewFullScreenButton"
                                variant="text"
                                onClick={openPresaveModal}
                            >
                                View full screen
                            </ButtonSymphony>
                            <div className="mobileButtonsContainerInner" style={sty.mobileButtonsContainerInner}>
                                {showShareButton &&
                                    <ButtonSymphony
                                        className="presaveLinkShareButton"
                                        variant="text"
                                        onClick={onClickShare}
                                    >
                                        Share
                                    </ButtonSymphony>
                                }
                                <ButtonSymphony
                                    className="presaveLinkCopyButton"
                                    onClick={() => onClickCopy('button')}
                                    variant="text"
                                >
                                    Copy
                                </ButtonSymphony>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    mobileButtonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    buttonsContainerPreSave: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 24,
    },
    preSaveLinkInnerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    preSaveLinkInnerContent_mobile: {
        flexDirection: 'row',
    },
    presaveLinkClickableContainer: {
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
        padding: 16,
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
        marginBottom: 0,
    },
    presaveLinkClickableContainer_mobile: {
        marginBottom: 16,
    },
    presaveLinkText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
        maxHeight: '100%',
        width: '100%',
    },
    mobileButtonsContainerInner: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
    }
};

export default PresaveLink;
